/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import React, { useCallback, useState } from 'react';

import { useIntl } from 'react-intl';

import { IconButton } from '@atlaskit/button/new';
import { cssMap, cx, jsx } from '@atlaskit/css';
import ArrowUpIcon from '@atlaskit/icon/core/arrow-up';
import Image from '@atlaskit/image';
import { Box, Inline } from '@atlaskit/primitives/compiled';
import { usePublish } from '@atlaskit/rovo-triggers/main';
import TextArea from '@atlaskit/textarea';
import { token } from '@atlaskit/tokens';

import { useAIAnswerContext } from '../../../ai-answer-dialog/ai-answer-context';
import { onOpenChatButtonClick, onSendChatInput } from '../../../ai-answer-dialog/analytics';
import {
	SainAction,
	useSAINUsageCriteriaMet,
} from '../../../common/controller/analytics/hooks/use-sain-usage';
import { useAnalytics } from '../../../common/controller/analytics/use-analytics';

import RovoIcon from './assets/Rovo.svg';
import { messages } from './messages';

const styles = cssMap({
	root: {
		borderRadius: token('radius.xlarge'),
		width: '100%',
		zIndex: 500,
		paddingTop: token('space.150'),
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.150'),
		backgroundColor: token('elevation.surface.sunken'),
		borderStyle: 'solid',
		borderWidth: token('border.width'),
		borderColor: token('color.border'),
		boxSizing: 'border-box',
		overflow: 'hidden',
		transition: 'display 1s allow-discrete, opacity 0.8s ease-out',
	},
	rootShownStyles: {
		display: 'block',
		opacity: '1',
	},
	rootHiddenStyles: {
		display: 'none',
		opacity: '0',
	},
	inputIconButtonWrapperStyles: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		zIndex: 600,
	},
});

export const OpenChatInputBox: () => JSX.Element | null = () => {
	const { formatMessage } = useIntl();
	const [fireAnalyticsEvent] = useAnalytics();
	const { fireSAINUsageEvent } = useSAINUsageCriteriaMet();
	const publish = usePublish('ai-mate');

	const [showOpenChatInput, setShowOpenChatInput] = useState(true);
	const [inputValue, setInputValue] = useState('');

	const { commonAttributes, showChatFollowUps } = useAIAnswerContext();
	const { answerString, query, searchSessionId } = commonAttributes;

	const isSendDisabled = !inputValue.trim() || inputValue.trim().length === 0;

	const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
		if (inputValue.trim() && event.key === 'Enter') {
			event.preventDefault();
			fireAnalyticsEvent(
				onSendChatInput({
					chatInputQuery: inputValue,
					...commonAttributes,
				}),
			);
			fireAnalyticsEvent(onOpenChatButtonClick({ ...commonAttributes }));
			fireSAINUsageEvent({ metCriteria: SainAction.OPEN_CHAT_CLICKED, commonAttributes });
			sendToChat();
		}
	};

	const sendToChat = useCallback(() => {
		publish({
			type: 'chat-new',
			source: 'sain',
			data: {
				name: query.substring(0, 30),
				dialogues: [
					{
						human_message: { content: query },
						agent_message: { content: answerString },
					},
				],
				prompt: inputValue,
				sourceId: searchSessionId,
			},
		});

		setShowOpenChatInput(false);
	}, [inputValue, query, searchSessionId, publish, answerString]);

	return showChatFollowUps ? (
		<Box
			xcss={cx(styles.root, showOpenChatInput ? styles.rootShownStyles : styles.rootHiddenStyles)}
		>
			<Inline alignBlock="center">
				<Box xcss={styles.inputIconButtonWrapperStyles}>
					<Image src={RovoIcon} alt="" />
				</Box>
				<TextArea
					placeholder={formatMessage(messages.ai_answer_open_chat_input_box_ask_rovo)}
					value={inputValue}
					onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setInputValue(e.target.value)}
					onKeyDown={handleKeyDown}
					appearance="none"
					minimumRows={1}
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
					style={{ paddingTop: '9px' }}
					testId="lumina-input-to-chat-text-area"
				/>
				<Box xcss={styles.inputIconButtonWrapperStyles}>
					<IconButton
						appearance="primary"
						isDisabled={isSendDisabled}
						shape="circle"
						icon={ArrowUpIcon}
						label="Send query"
						onClick={sendToChat}
						testId="lumina-input-to-chat-submit"
					/>
				</Box>
			</Inline>
		</Box>
	) : null;
};
